/**
 * Aviso de cookies DISCRETO (LGPD) — volta do banner removido em 12/10/2026.
 *
 * O antigo era um cartão grande no rodapé e cobria a base da tela no 1º acesso;
 * sem ele a Auditoria LGPD parou de receber registro (dono, 28/10/2026: "voltar
 * com um aviso discreto"). Agora é uma faixa de UMA linha, acima do menu de
 * baixo, que aparece uma vez por aparelho, não bloqueia nada (o resto da tela
 * continua clicável) e fica por baixo de qualquer janela do app.
 *
 * "Ok" grava ACCEPTED e "Só os necessários" grava REJECTED — no aparelho
 * (`aliv_lgpd_consent`) e em POST /api/system/consent, com o token do cliente
 * quando ele já está logado (o servidor liga o registro ao cadastro).
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { t, useI18n } from '../i18n';

type WinIds = Window & { STORE_ID?: number | string; STORE_SLUG?: string };
const CONSENT_KEY = 'aliv_lgpd_consent';

export function CookieAvisoIsland() {
  useI18n();
  const [visivel, setVisivel] = useState(false);

  useEffect(() => {
    try { if (localStorage.getItem(CONSENT_KEY)) return; } catch { /* sem storage: mostra */ }
    const tm = window.setTimeout(() => setVisivel(true), 2500);
    return () => window.clearTimeout(tm);
  }, []);

  const responder = (status: 'ACCEPTED' | 'REJECTED') => {
    try { localStorage.setItem(CONSENT_KEY, JSON.stringify({ status, date: new Date().toISOString() })); } catch { /* */ }
    setVisivel(false);
    const hdr: Record<string, string> = { 'Content-Type': 'application/json' };
    try {
      const tk = localStorage.getItem('wabiz_token');
      if (tk) hdr.Authorization = 'Bearer ' + tk;
    } catch { /* */ }
    fetch('/api/system/consent', {
      method: 'POST',
      headers: hdr,
      body: JSON.stringify({
        consent_type: 'COOKIE_BANNER',
        status,
        store_id: (window as WinIds).STORE_ID,
        user_agent: navigator.userAgent,
      }),
    }).catch(() => { /* o aviso já sumiu; o registro tenta de novo no próximo aparelho */ });
  };

  if (!visivel) return null;
  const slug = (window as WinIds).STORE_SLUG || '';

  return createPortal(
    // pointer-events só no cartão: a faixa nunca trava a sacola nem o menu de baixo.
    <div className="fixed left-0 right-0 z-[900] pointer-events-none px-3"
         style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)' }}>
      <div className="pointer-events-auto mx-auto max-w-md flex items-center gap-2 rounded-full bg-slate-900/95 text-slate-200 text-[11px] leading-tight pl-3 pr-1.5 py-1.5 shadow-lg border border-slate-700">
        <span className="flex-1 min-w-0">
          🍪 {t('Usamos cookies para o app funcionar.')}{' '}
          <a href={`/privacy/${slug}`} target="_blank" rel="noreferrer" className="underline text-slate-300">{t('Política de Privacidade')}</a>
          {' · '}
          <button type="button" onClick={() => responder('REJECTED')} className="underline text-slate-400">{t('Só os necessários')}</button>
        </span>
        <button type="button" onClick={() => responder('ACCEPTED')}
                className="shrink-0 bg-white text-slate-900 font-bold rounded-full px-3 py-1">
          Ok
        </button>
      </div>
    </div>,
    document.body,
  );
}
